import template from './candlediagram.tpl';
import DomUtil from '@/core/utils/domUtil';
import ViewportUtility from '@/core/utils/viewport/viewportUtil';
import {gsap} from 'gsap';
import styles from './candlediagram.module.scss';
import CohortTableModel from './../../model/cohortTableModel';
import ContentModel from "@/app/model/contentModel";
import AppStateModel from "@/app/model/appStateModel";

const _XMLNS = 'http://www.w3.org/2000/svg';
const _CANDLE_SECTION_SIZE = 5;
const _CANDLE_SPACING = {x: 3.3, y: 0};
const _CANDLES_MATRIX = {
    small: {xScale: 1.038, yScale: 1.071, xOffset: 13.5, yOffset: 18.1},
    medium: {xScale: 1.307, yScale: 1.338, xOffset: 17, yOffset: 7.7},
    large: {xScale: 1.307, yScale: 1.536, xOffset: 17, yOffset: 0}
};

// candle: path component elements
const _CANDLE_BASE = 'M10.74,12.75H7.25V6.94a.72.72,0,0,0-.75-.67.72.72,0,0,0-.75.67v5.8H2.26a2.15,2.15,0,0,0-2.26,2V38.33A.72.72,0,0,0,.75,39H12.25a.72.72,0,0,0,.75-.67V14.75A2.15,2.15,0,0,0,10.74,12.75Z';
const _CANDLE_FLAME = 'M3.88,10.29a.81.81,0,0,0,.45-.13.62.62,0,0,0,.15-.94A4.82,4.82,0,0,1,3.3,6.08,5.21,5.21,0,0,1,6.54,1.41,5.21,5.21,0,0,1,9.77,6.08,4.82,4.82,0,0,1,8.6,9.22a.62.62,0,0,0,.15.94A.82.82,0,0,0,9.8,10a6.05,6.05,0,0,0,1.48-3.94,6.6,6.6,0,0,0-4.44-6,.84.84,0,0,0-.6,0,6.6,6.6,0,0,0-4.44,6A6.05,6.05,0,0,0,3.27,10,.79.79,0,0,0,3.88,10.29Z';
const _CANDLE_FILL = 'M11.49,37.66h-10V14.75a.72.72,0,0,1,.75-.67h8.48a.72.72,0,0,1,.75.67Z';


export default class CandleDiagram {
    /*
     TODO this class does not currently generate a diagram as it's supposed to, because of some bug which prevents attributes and updates to take effect.
     Currently uses a pre-made static SVG found in template .twig (copied from generated svg markup)
     */

    private data: any;
    private template: string;
    private common: any;
    private animationElements: any;
    private currentFill: string;
    private candles_top!: HTMLElement;
    private candles_bottom!: HTMLElement;
    private scale!: HTMLElement;
    private element!: HTMLElement | null;
    private _target!: HTMLElement;
    private svg!: HTMLElement;
    private results_top!: HTMLElement;
    private results_top_val!: HTMLElement;
    private results_bottom!: HTMLElement;
    private results_bottom_val!: HTMLElement;
    private diagram_wrapper!: HTMLElement;

    constructor(data) {
        // trim off "-" characters for breakpoints where diagram is hidden.
        let labels: any = {};
        if(ViewportUtility.breakpoint !== 'w880') {
            labels.top = data.diagram.label_top.replace(/-/g, '');
            labels.bottom = data.diagram.label_bottom.replace(/-/g, '');
        } else {
            labels.top = data.diagram.label_top;
            labels.bottom = data.diagram.label_bottom;
        }

        this.data = data;
        this.common = ContentModel.instance.getCommonData();
        this.currentFill = this.getCurrentFill()
        this.template = template({data: this.data, styles: styles, labels_override: labels});
    }

    init() {

        // TODO disabled because of some obscure bug, perhaps webpack causes some desync between shadow DOM and DOM. Bottom line is some attributes never take effect. We can bypass this with svg CSS-transforms, but that leaves out IE/EDGE.
        //this.createDiagram();

        /* TODO if you turn above on again, make sure to replace the twig ".diagram" svg with below markup to generate on a "clean" svg:
         <svg class="{{ styles.diagram }}" view-box="0 0 588 200" version="1.1" xmlns="http://www.w3.org/2000/svg"
         xmlns:xlink="http://www.w3.org/1999/xlink">
         <g id="{{ styles.candles_top }}"></g>
         <g id="{{ styles.scale }}" transform="translate(0,105)"></g>
         <g id="{{ styles.candles_bottom }}"></g>
         </svg>
         */

        this.animationElements = {
            candles_top: this.candles_top.querySelectorAll('.candle'),
            candles_top_fills: this.candles_top.querySelectorAll('.base_fill'),
            candles_bottom: this.candles_bottom.querySelectorAll('.candle'),
            candles_bottom_fills: this.candles_bottom.querySelectorAll('.base_fill'),
            scale: this.scale.querySelectorAll('text')
        };
        this.resetCandleColors();
    }

    setSVGAttributes(element, attributes) {

        for (let prop in attributes) {
            element.setAttributeNS(_XMLNS, prop, attributes[prop]);
        }
    }

    drawText(target, x, y, value, styleClass = 'scale') {

        let text = document.createElementNS(_XMLNS, 'text');
        let properties = {
            class: styleClass,
            x: x,
            y: y,
        };

        this.setSVGAttributes(text, properties);
        text.textContent = value;
        target.appendChild(text);

        return text;
    }

    drawLine(target, x1, y1, x2, y2, styleClass = 'scale') {

        let xCandleHalf = 8;

        let line = document.createElementNS(_XMLNS, 'line');
        let properties = {
            class: styleClass,
            x1: x1 + xCandleHalf,
            y1: y1,
            x2: x2 + xCandleHalf,
            y2: y2
        };
        this.setSVGAttributes(line, properties);
        target.appendChild(line);

        return line;
    }

    getScaleYears(amount, maxYear, interval = _CANDLE_SECTION_SIZE) {

        // Each candle indicate 1 year, and the scale texts are placed at the middle candle for each section.
        // So, we reduce the maxYear by half of the candles section size to get the highest scale-value, and go
        // backwards from there.
        // (maxYear = 103, interval = 5) => 103 - 2 (to go back to mid-candle) => 101   =>   [76, 81, 86, 91, 96, 101]
        let results: number[] = [];
        let start = maxYear - Math.floor(interval / 2);
        for (let i = 0; i < amount; i++) {
            results.push(start - (i * interval))
        }

        return results.reverse();
    }

    setScaleYears(maxYear) {

        let texts = this.scale.querySelectorAll('text');
        let years = this.getScaleYears(texts.length, maxYear);
        for (let i = 0; i < years.length; i++) {
            texts[i].innerHTML = years[i].toString();
        }
    }

    createScale(target, xOffsets, maxYear = 92, yOffset = 0) {

        let years = this.getScaleYears(xOffsets.length, maxYear);

        for (let i = 0; i < xOffsets.length; i++) {

            this.drawLine(target, xOffsets[i], -20 + yOffset, xOffsets[i], -35 + yOffset);
            this.drawText(target, xOffsets[i], yOffset, years[i]);
            this.drawLine(target, xOffsets[i], 8 + yOffset, xOffsets[i], 23 + yOffset);
        }

    }

    createCandleSVG(className) {
        /*
         This function is a replacement alternative for imported partial candle svg.
         */

        // create parent svg
        let cSvg = document.createElementNS(_XMLNS, 'svg');
        this.setSVGAttributes(cSvg, {'class': className});

        // create component elements
        let cG = document.createElementNS(_XMLNS, 'g');
        let cBase = document.createElementNS(_XMLNS, 'path');
        let cFlame = document.createElementNS(_XMLNS, 'path');
        let cFill = document.createElementNS(_XMLNS, 'path');

        // add shape attributes to path elements
        this.setSVGAttributes(cBase, {'d': _CANDLE_BASE, 'class': 'base', 'shape-rendering': 'optimizeQuality'});
        this.setSVGAttributes(cFlame, {'d': _CANDLE_FLAME, 'class': 'flame', 'shape-rendering': 'optimizeQuality'});
        this.setSVGAttributes(cFill, {'d': _CANDLE_FILL, 'class': 'base_fill', 'shape-rendering': 'optimizeQuality'});

        // compose SVG
        cSvg.appendChild(cG);
        cG.appendChild(cBase);
        cG.appendChild(cFlame);
        cG.appendChild(cFill);

        return cSvg;
    }

    createCandleBar(target, yBaseOffset = 0, callback, sections = 6) {

        // we want these positions when generating a scale (retrieved through callback)
        let largeCandleOffsets: any[] = [];


        // make sure we can scale without aspect-ratio intact.
        target.style.preserveAspectRatio = 'none';


        // insert candle-content
        for (let i = 0; i < sections * _CANDLE_SECTION_SIZE; i++) {
            let candle = this.createCandleSVG('candle');
            target.appendChild(candle);
        }


        // get DOM references to new content (one g element in each candle svg)
        let candles = target.querySelectorAll('g > svg > g');


        // resolve positions and scale ...
        let c, xOffset, yOffset;
        xOffset = yOffset = 0;

        /* To describe what happens here:
         We position candles with increasing & decreasing sizes, by sets of 5 (with 3 being the largest in the middle).
         The candles are generated from the left, and positioned with offsets depending their sizes. The horizontal
         offset (xOffset) is persistent and incremented by width + spacing for each generated candle. Vertical offset
         resets after each positioned candle.
         */
        for (let i = 0; i < sections * _CANDLE_SECTION_SIZE; i++) {
            // add static spacing to each candle
            xOffset += _CANDLE_SPACING.x;
            yOffset = _CANDLE_SPACING.y + yBaseOffset;

            switch (i % 5) {
                case 0:
                    // special case, where we want to add a little extra spacing at the start of each section (after the first).
                    // (intentionally omitting break, we want to run next case as well)
                    if (i > 0) xOffset += _CANDLE_SPACING.x;
                case 4: // eslint-disable-line
                    // small
                    c = _CANDLES_MATRIX.small;

                    break;
                case 1:
                case 3:
                    // medium
                    c = _CANDLES_MATRIX.medium;

                    break;
                case 2:
                    // large
                    c = _CANDLES_MATRIX.large;
                    largeCandleOffsets.push(xOffset);

                // end switch / no default
            }

            // apply dimension variables to candle-element (position relative to current one, and scale)
            candles[i].setAttributeNS(_XMLNS, 'transform', `
                translate(${xOffset}, ${c.yOffset + yOffset}) 
                scale(${c.xScale}, ${c.yScale})`
            );

            // increment horizontal position-offset for next candle based on the width of the current
            xOffset += c.xOffset;
        }

        if (typeof callback === 'function') callback(largeCandleOffsets);
    }

    resetCandleColors() {
        for (let topC of this.animationElements.candles_top) topC.style.fill = this.common.colors["icon-dark"];
        for (let topF of this.animationElements.candles_top_fills) topF.style.fill = this.common.colors["white"];
        for (let bottomC of this.animationElements.candles_bottom) bottomC.style.fill = this.common.colors["icon-dark"];
        for (let bottomF of this.animationElements.candles_bottom_fills) bottomF.style.fill = this.common.colors["white"];
    }

    createDiagram() {

        this.createCandleBar(this.candles_top, 0, null);
        this.createCandleBar(this.candles_bottom, 140, (offsets) => {
            this.createScale(this.scale, offsets)
        });
    }

    destroy() {
        this._killAnimations();

        if (this.element && DomUtil.isDescendant(this._target, this.element)) {
            this._target.removeChild(this.element);
        }

        this.element = null;
    }

    _killAnimations() {
        gsap.killTweensOf(this.element);
        Object.values(this.animationElements).forEach((el: HTMLElement) => gsap.killTweensOf(el));
    }

    update(delay) {

        this._killAnimations();

        this.resetCandleColors();
        this.currentFill = this.getCurrentFill()

        let results = CohortTableModel.getResultsByBirthdayAndGender();

        let topValue = Math.round(results['cohort']);
        let bottomValue = Math.round(results['period']);

        this.animate(topValue, bottomValue, delay);
    }

    getCurrentFill() {
        let isMale = AppStateModel.gender === 'm'
        return isMale ? this.common.colors["cohort-candle-m"] : this.common.colors["cohort-candle-w"];
    }

    animate(topValue, bottomValue, delay = 0, start = 0.02, easeBase = 0.001) {

        // decide top/bottom animation timings on value comparison (highest is always full)
        let highest, lowest, fullBar, fullFills, partialBar, partialFills;
        if (topValue >= bottomValue) {
            highest = topValue;
            fullBar = this.animationElements.candles_top;
            fullFills = this.animationElements.candles_top_fills;
            lowest = bottomValue;
            partialBar = this.animationElements.candles_bottom;
            partialFills = this.animationElements.candles_bottom_fills;
        }
        else {
            highest = bottomValue;
            fullBar = this.animationElements.candles_bottom;
            fullFills = this.animationElements.candles_bottom_fills;
            lowest = topValue;
            partialBar = this.animationElements.candles_top;
            partialFills = this.animationElements.candles_top_fills;
        }



        // no animation for scale and results, just setting values.
        this.results_top_val.innerHTML = topValue;
        this.results_top.style.color = this.currentFill;
        this.results_bottom_val.innerHTML = bottomValue;
        this.results_bottom.style.color = this.currentFill;
        this.setScaleYears(highest);

        let isMale = AppStateModel.gender === 'm'
        let fill = isMale ? this.common.colors["cohort-candle-m"] : this.common.colors["cohort-candle-w"];

        // all animations are done in parallel
        let reduceBy = highest - lowest;
        let fullEase, partialEase;
        for (let i = 0; i < fullBar.length; i++) {
            fullEase = (i / fullBar.length) * easeBase;

            gsap.to(fullBar[i], 0, {delay: delay + (start * i) + ((fullEase * i) * i), fill: this.currentFill});
            gsap.to(fullFills[i], 0, {delay: delay + (start * i) + ((fullEase * i) * i), fill: this.currentFill});

            if (i < partialBar.length - reduceBy) {
                partialEase = (i / (partialBar.length - reduceBy)) * easeBase;

                gsap.to(partialBar[i], 0, {delay: delay + (start * i) + ((partialEase * i) * i), fill: this.currentFill});
                gsap.to(partialFills[i], 0, {
                    delay: delay + (start * i) + ((partialEase * i) * i),
                    fill: this.currentFill
                });
            }
        }

    }

    appendTo(target) {

        this._target = target;
        this._target.innerHTML += this.template;

        // get DOM references to key elements
        this.element = this._target.lastElementChild as HTMLElement;
        this.svg = this.element.querySelectorAll('.diagram')[0] as HTMLElement;
        this.candles_top = this.svg.querySelector('#candles_top') as HTMLElement;
        this.candles_bottom = this.svg.querySelector('#candles_bottom') as HTMLElement;
        this.scale = this.svg.querySelector('#scale') as HTMLElement;
        this.results_top = this.element.querySelector('.results label:first-of-type') as HTMLElement;
        this.results_top_val = this.results_top.querySelector('.value') as HTMLElement;
        this.results_bottom = this.element.querySelector('.results label:last-of-type') as HTMLElement;
        this.results_bottom_val = this.results_bottom.querySelector('.value') as HTMLElement;
        this.diagram_wrapper = this.element.querySelector('.diagram_wrapper') as HTMLElement;
    }
}
